import { Ball, Vector2D, BallStyle } from '@/types';
import { parseHex } from '@/lib/colorUtils';

export class BallPool {
  private pool: Ball[] = [];

  constructor(initialSize: number = 1000) {
    for (let i = 0; i < initialSize; i++) {
      this.pool.push(this.createEmptyBall());
    }
  }

  private createEmptyBall(): Ball {
    return {
      id: '',
      pos: { x: 0, y: 0 },
      vel: { x: 0, y: 0 },
      radius: 0,
      color: '#ffffff',
      mass: 1,
      angle: 0,
      trail: [],
      lastSpawnTime: 0,
      lastTrailTime: 0,
      trailAccumulator: 0,
      isBox: false
    };
  }

  get(
    id: string,
    pos: Vector2D,
    vel: Vector2D,
    radius: number,
    color: string,
    mass: number,
    style?: BallStyle,
    isBox?: boolean,
    overrides?: Partial<Ball>
  ): Ball {
    let ball = this.pool.pop();
    if (!ball) {
      ball = this.createEmptyBall();
    }

    ball.id = id;
    ball.pos.x = pos.x;
    ball.pos.y = pos.y;
    ball.vel.x = vel.x;
    ball.vel.y = vel.y;
    ball.radius = radius;
    ball.color = color;
    ball.mass = mass;
    ball.style = style;
    ball.isBox = isBox;
    ball.angle = 0;
    ball.rotationSpeed = 0;
    
    // Cache normalized RGB for worker consumption
    const rgb = parseHex(color);
    ball.rgb = [rgb[0] / 255, rgb[1] / 255, rgb[2] / 255];

    ball.spawnTime = overrides?.spawnTime || Date.now();
    ball.originalAngle = undefined;
    ball.trail = [];
    ball.lastSpawnTime = Date.now();
    ball.lastTrailTime = 0;
    ball.trailAccumulator = 0;
    ball.hitPoints = [];
    ball.isBreaking = false;
    ball.breakStartTime = undefined;
    ball.isDebris = false;

    // New Spawner / Lifecycle properties
    ball.generation = overrides?.generation || 0;
    ball.lifespan = overrides?.lifespan;
    ball.maxLifespan = overrides?.maxLifespan;
    ball.opacity = overrides?.opacity ?? 1.0;
    ball.isFading = overrides?.isFading ?? false;
    
    // Reset optional properties
    ball.shape = overrides?.shape;
    ball.dimensions = overrides?.dimensions;
    ball.originalPos = overrides?.originalPos;

    return ball;
  }

  release(ball: Ball) {
    // Reset properties to avoid memory leaks or stale data
    ball.trail = [];
    ball.hitPoints = [];
    this.pool.push(ball);
  }

  getAvailableCount(): number {
    return this.pool.length;
  }
}
